import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Chip, debounce } from '@mui/material';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'src/redux/store';
import { setCreatePolicyClicked } from 'src/features/policyengine/policyEngineSlice';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type groupData = {
	id: string;
	name: string | null;
	email?: string | null;
	type?: string | null;
};
type userData = {
	id: string;
	name: string | null;
	email?: string | null;
	remoteId?: string | null;
	barracudaId?: string | null;
};
type SelectedGroups = {
	current: groupData[];
};

type SelectedUsers = {
	current: userData[];
};

type IdentityProps = {
	selectedUsers: SelectedUsers;
	setError: (error: boolean) => void;
	selectedGroups: SelectedGroups;
};

function IdentityUsers({ selectedUsers, setError, selectedGroups }: IdentityProps) {
	const [data, setData] = React.useState<userData[]>([]);
	const [selectedTags, setSelectedTags] = React.useState(() =>
		selectedUsers.current[0]?.id === 'All'
			? []
			: selectedUsers.current.map((user) => ({ id: user.id, name: user.name }))
	);
	const clearedAllusers = React.useRef(false);
	const prevUsersData = React.useRef<userData[]>([]);
	const [isEmptyRes, setIsEmptyRes] = React.useState(false);
	const [inputValue, setInputValue] = React.useState('');

	const dispatch = useDispatch<AppDispatch>();
	const { createPolicyClicked } = useSelector((state: RootState) => state.policyEngine);

	const { api } = useAppToolchain();
	const { auth } = useAuth();

	const allUsers = [
		{
			id: 'All',
			name: null,
			email: null,
		},
	];

	const handleAllGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setSelectedTags(checked ? data.map((user) => ({ id: user.id, name: user.name })) : []);
		selectedUsers.current = checked ? allUsers : [];
	};

	const handleAllGroupLabelClick = (event: React.MouseEvent<HTMLLIElement>) => {
		const target = event.target as HTMLElement;
		if (!target?.firstChild) return;

		const checkbox = (target as any).children?.[0]?.firstChild as HTMLInputElement;
		if (!checkbox) return;

		const { checked } = checkbox;
		setSelectedTags(!checked ? data.map((user) => ({ id: user.id, name: user.name })) : []);
		selectedUsers.current = !checked ? allUsers : [];
	};

	const handleAllDelete = () => {
		setSelectedTags([]);
		selectedUsers.current = [];
	};

	React.useEffect(() => {
		performSearch('');

		return () => {
			prevUsersData.current = [];
			setData([]);
		};
	}, []);

	function convertData(data: userData[]) {
		return data.reduce(
			(acc, curr) => {
				if (!acc[curr.id] && curr.id !== 'All') {
					acc[curr.id] = curr;
				}
				return acc;
			},
			{} as Record<string, userData>
		);
	}

	const performSearch = React.useMemo(
		() =>
			debounce((query: string) => {
				api.getPolicyUsers(auth.accountDetails.account.id, query).then((res) => {
					if (res) {
						if (res.data.length === 0) {
							setIsEmptyRes(true);
						} else {
							setIsEmptyRes(false);
						}
						const userData = res.data.map((ele: any) => ele.user);
						const hash = convertData([...userData, ...selectedUsers.current]);
						setData(Object.values(hash));

						if (prevUsersData.current.length === 0) {
							prevUsersData.current = userData;
						}

						if (selectedUsers.current[0]?.id === 'All') {
							setSelectedTags(() => userData.map((user: userData) => ({ id: user.id, name: user.name })));
							selectedUsers.current = allUsers;
						}

						if (createPolicyClicked) {
							setSelectedTags(userData);
							selectedUsers.current = allUsers;
							dispatch(setCreatePolicyClicked(false));
						}
					}
				});
			}, 500),
		[api, auth.accountDetails.account.id, createPolicyClicked, dispatch]
	);

	const handleInputChange = (e: React.SyntheticEvent, value: string) => {
		if (selectedUsers.current[0]?.id === 'All') {
			return;
		}
		const query = value.trimStart();
		setInputValue(query);
		performSearch(query);
	};

	const handleInputKeyDown = (event: React.KeyboardEvent) => {
		if (event.keyCode === 8 && selectedTags.length === prevUsersData.current.length) {
			clearedAllusers.current = true;
		}
	};

	const handleBlur = () => {
		if (inputValue.length > 0) {
			performSearch('');
		}
		setInputValue('');

		if (selectedTags.length === 0 && selectedGroups.current.length === 0) {
			setError(true);
		} else {
			setError(false);
		}
	};

	const checkOptionSelected = (option: userData) => {
		const obj = selectedTags.find((user) => user.id === option.id);
		return obj?.id === option.id;
	};

	const filterOptions = (options: any[]) => {
		return options;
	};

	return (
		<>
			<Autocomplete
				multiple
				id="checkboxes-tags-demo"
				options={[{ id: '-1', name: 'All Users', email: '', type: '' }, ...data]}
				limitTags={1}
				getLimitTagsText={(tags) => (
					<span style={{ fontSize: '12px', color: 'gray', fontWeight: 500 }}>{`+${tags} more`}</span>
				)}
				disableCloseOnSelect
				clearOnBlur={false}
				value={selectedTags
					.map((user) => data.find((option) => option.id === user.id))
					.filter((option) => option != null)}
				getOptionLabel={(option) => option?.name || ''}
				inputValue={inputValue}
				filterOptions={filterOptions}
				onInputChange={handleInputChange}
				onChange={(_, newValue) => {
					setInputValue(inputValue);
					if (clearedAllusers.current) {
						setSelectedTags([]);
						selectedUsers.current = [];
						clearedAllusers.current = false;
					} else {
						setSelectedTags(
							newValue
								.filter((ele) => ele.name !== 'All Users')
								.map((user) => ({ id: user.id, name: user.name }))
						);
						selectedUsers.current = newValue.filter((ele) => ele.name !== 'All Users') as userData[];
						if (selectedUsers.current.length === prevUsersData.current.length) {
							selectedUsers.current = allUsers;
						}
					}
				}}
				renderOption={(props, option) => {
					return (
						<div key={option.id}>
							{option?.name === 'All Users' && inputValue.length === 0 && !isEmptyRes && (
								<li {...props} onClick={handleAllGroupLabelClick}>
									<Checkbox
										id="all"
										icon={icon}
										checkedIcon={checkedIcon}
										checked={
											selectedTags.length === prevUsersData.current.length &&
											prevUsersData.current.length !== 0 &&
											!isEmptyRes
										}
										onChange={handleAllGroupChange}
										sx={{
											color: 'black',
											'&.Mui-checked': {
												color: 'black',
											},
										}}
									/>
									All Users
								</li>
							)}

							{option?.name !== 'All Users' && (
								<li style={{ marginLeft: '12px' }} {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={checkOptionSelected(option as userData)}
										sx={{
											color: 'black',
											'&.Mui-checked': {
												color: 'black',
											},
										}}
									/>
									{option?.name}
								</li>
							)}
						</div>
					);
				}}
				size="small"
				renderInput={(params) => {
					return (
						<TextField
							{...params}
							label="Users"
							onKeyDown={handleInputKeyDown}
							InputLabelProps={{
								style: { fontSize: '10px', fontWeight: 'bold', lineHeight: 2 },
							}}
							onBlur={handleBlur}
						/>
					);
				}}
				renderTags={(values, getTagProps) => {
					if (values[0] == null) return [];
					const renderedTags: React.ReactNode[] = [];
					if (
						selectedTags.length === prevUsersData.current.length &&
						prevUsersData.current.length !== 0 &&
						!isEmptyRes
					) {
						renderedTags.push(
							<Chip
								sx={{
									fontSize: '10px',
									height: '20px',
									fontWeight: 500,
								}}
								size="small"
								key="all-users"
								label="All Users"
								onDelete={handleAllDelete}
							/>
						);
					} else {
						values.forEach((option, index) => {
							renderedTags.push(
								<Chip
									sx={{
										fontSize: '10px',
										height: '20px',
										fontWeight: 500,
									}}
									size="small"
									key={option?.id}
									label={option?.name}
									{...getTagProps({ index })}
								/>
							);
						});
					}
					return renderedTags;
				}}
			/>
		</>
	);
}

export default IdentityUsers;
